import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Star, CheckCircle2, ChevronRight, LogOut } from "lucide-react";

const EXAMS = [
  {
    id: "NDA",
    name: "NDA",
    full: "National Defence Academy",
    emoji: "⚔️",
    color: "#1E40AF",
    light: "#EFF6FF",
    border: "#BFDBFE",
    badge: "Most Popular",
    eligibility: "10+2 / Appearing",
    age: "16.5 – 19.5 yrs",
    freq: "Twice a year",
    seats: "~400 seats",
    subjects: ["Maths", "GAT (English, GK, Physics, Chemistry, History, Geography)"],
    desc: "Join the prestigious NDA and serve in the Army, Navy or Air Force. Written + SSB.",
  },
  {
    id: "CDS",
    name: "CDS",
    full: "Combined Defence Services",
    emoji: "🎖️",
    color: "#065F46",
    light: "#ECFDF5",
    border: "#A7F3D0",
    badge: "For Graduates",
    eligibility: "Graduate (any stream)",
    age: "19 – 25 yrs",
    freq: "Twice a year",
    seats: "~450 seats",
    subjects: ["English", "General Knowledge", "Elementary Mathematics (IMA/INA only)"],
    desc: "Entry into IMA, INA, AFA and OTA after graduation. Written + SSB interview.",
  },
  {
    id: "AFCAT",
    name: "AFCAT",
    full: "Air Force Common Admission Test",
    emoji: "✈️",
    color: "#1D4ED8",
    light: "#EFF6FF",
    border: "#BAE6FD",
    badge: "IAF Entry",
    eligibility: "Graduate (60%+)",
    age: "20 – 26 yrs",
    freq: "Twice a year",
    seats: "Flying + Ground Duty",
    subjects: ["General Awareness", "Verbal Ability", "Numerical Ability", "Reasoning"],
    desc: "Commission into the Indian Air Force in Flying and Ground Duty branches.",
  },
  {
    id: "CAPF",
    name: "CAPF",
    full: "Central Armed Police Forces",
    emoji: "🛡️",
    color: "#7C3AED",
    light: "#F5F3FF",
    border: "#DDD6FE",
    badge: "AC Level",
    eligibility: "Graduate (any stream)",
    age: "20 – 25 yrs",
    freq: "Once a year",
    seats: "BSF, CRPF, CISF, SSB, ITBP",
    subjects: ["General Studies", "Essay", "General Ability & Intelligence"],
    desc: "Assistant Commandant in para-military forces. Written exam + Physical + Interview.",
  },
  {
    id: "TA",
    name: "TA",
    full: "Territorial Army",
    emoji: "🪖",
    color: "#92400E",
    light: "#FFFBEB",
    border: "#FDE68A",
    badge: "Civilian + Army",
    eligibility: "Graduate + Employed",
    age: "18 – 42 yrs",
    freq: "Once a year",
    seats: "Serve while working",
    subjects: ["Reasoning & Elementary Maths", "General Knowledge", "English"],
    desc: "Serve your nation while maintaining your civilian career. Part-time soldiering.",
  },
];

export default function ExamSelectPage({ onSelect, onLogout, userName }) {
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState(null);

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onSelect(selected);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A15] flex flex-col font-body overflow-x-hidden"
      style={{ paddingTop: "env(safe-area-inset-top)" }}>

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl overflow-hidden shrink-0">
            <img src="/logo192.png" alt="EduPulse" className="w-full h-full object-cover" />
          </div>
          <span className="font-sora font-extrabold text-white text-[18px]">
            EduPulse<span className="text-[#C9A227]">AI</span>
          </span>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-1.5 text-white/30 hover:text-white/60 text-xs font-medium transition-colors bg-transparent border-none cursor-pointer"
        >
          <LogOut size={14} />
          Sign out
        </button>
      </div>

      {/* Hero */}
      <div className="text-center pt-10 pb-6 px-5">
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/20 mb-5">
            <Shield size={13} className="text-[#C9A227]" />
            <span className="text-[#C9A227] text-[11px] font-bold font-sora tracking-wider uppercase">
              {userName ? `Welcome, ${userName.split(' ')[0]}` : 'Welcome to EduPulse Defence'}
            </span>
          </div>
          <h1 className="font-sora font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-2xl mx-auto">
            Which exam are you
            <br />
            <span style={{ background: "linear-gradient(90deg, #C9A227, #F59E0B)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              preparing for?
            </span>
          </h1>
          <p className="text-white/35 text-[15px] mt-4 max-w-md mx-auto leading-relaxed">
            We'll personalise your Current Affairs, GK topics, and practice MCQs based on your exam.
          </p>
        </motion.div>
      </div>

      {/* Exam cards */}
      <div className="flex-1 px-4 sm:px-6 lg:px-8 pb-36 max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {EXAMS.map((exam, i) => {
            const isSelected = selected === exam.id;
            const isHovered = hovered === exam.id;

            return (
              <motion.button
                key={exam.id}
                onClick={() => setSelected(exam.id)}
                onMouseEnter={() => setHovered(exam.id)}
                onMouseLeave={() => setHovered(null)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="text-left w-full rounded-2xl p-5 border-2 transition-all duration-200 cursor-pointer relative overflow-hidden"
                style={{
                  background: isSelected
                    ? `linear-gradient(135deg, ${exam.color}22, ${exam.color}10)`
                    : "rgba(255,255,255,0.03)",
                  borderColor: isSelected
                    ? exam.color
                    : isHovered
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(255,255,255,0.06)",
                  boxShadow: isSelected ? `0 0 0 1px ${exam.color}40, 0 8px 32px ${exam.color}20` : "none",
                }}
              >
                {/* Selected check */}
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      className="absolute top-3 right-3"
                    >
                      <CheckCircle2 size={20} style={{ color: exam.color }} />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Badge */}
                {exam.badge && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mb-3"
                    style={{ background: `${exam.color}20`, border: `1px solid ${exam.color}30` }}>
                    <Star size={9} style={{ color: exam.color }} />
                    <span className="text-[10px] font-bold font-sora" style={{ color: exam.color }}>
                      {exam.badge}
                    </span>
                  </div>
                )}

                {/* Exam name */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{exam.emoji}</span>
                  <div>
                    <div className="font-sora font-extrabold text-2xl text-white leading-none">{exam.name}</div>
                    <div className="text-[11px] text-white/40 mt-0.5 font-medium">{exam.full}</div>
                  </div>
                </div>

                <p className="text-[12px] text-white/50 leading-relaxed mb-4">{exam.desc}</p>

                {/* Key stats */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Eligibility", val: exam.eligibility },
                    { label: "Age", val: exam.age },
                    { label: "Frequency", val: exam.freq },
                    { label: "Seats", val: exam.seats },
                  ].map(({ label, val }) => (
                    <div key={label} className="rounded-xl p-2.5"
                      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div className="text-[9px] font-bold text-white/25 uppercase tracking-wider mb-0.5">{label}</div>
                      <div className="text-[11px] font-semibold text-white/70 leading-tight">{val}</div>
                    </div>
                  ))}
                </div>

                {/* Subjects */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {exam.subjects.map(s => (
                    <span key={s} className="text-[9px] font-medium px-2 py-0.5 rounded-full"
                      style={{ background: `${exam.color}15`, color: `${exam.color}cc`, border: `1px solid ${exam.color}20` }}>
                      {s.length > 22 ? s.slice(0, 22) + "…" : s}
                    </span>
                  ))}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Sticky CTA */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 px-4 py-4 sm:px-6"
            style={{
              background: "linear-gradient(to top, #050A15 60%, transparent)",
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}
          >
            <div className="max-w-sm mx-auto">
              <button
                onClick={handleConfirm}
                disabled={saving}
                className="w-full py-4 px-6 rounded-2xl border-none text-white font-sora font-bold text-base cursor-pointer
                           flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #C9A227, #D97706)",
                  boxShadow: "0 8px 32px rgba(201,162,39,0.4)",
                }}
              >
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Setting up your dashboard…
                  </>
                ) : (
                  <>
                    Start preparing for {selected}
                    <ChevronRight size={18} />
                  </>
                )}
              </button>
              <p className="text-center text-white/25 text-[11px] mt-2">
                You can change your exam anytime from your profile
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
